import React, { useState, useEffect } from 'react';
import { t } from '../i18n';

/**
 * Countdown display.
 * Shows hand icon, "Hold your phone up!", countdown number, and "Keep your screen on".
 * Number turns red at 3 seconds or fewer.
 *
 * @param {number} targetTime - Unix ms when show starts (countdown reaches 0)
 * @param {function} getServerTime - Returns synced server time in ms
 * @param {boolean} paused - Operator paused the countdown: hold the number (MM #190)
 * @param {number} remainingMs - Frozen remaining ms to show while paused
 */
export default function Countdown({ targetTime, getServerTime, paused = false, remainingMs = null }) {
  const [secondsLeft, setSecondsLeft] = useState(null);

  useEffect(() => {
    // Paused: freeze on the frozen remainder — no ticking (MM #190).
    if (paused) {
      setSecondsLeft(Math.max(0, Math.ceil((remainingMs || 0) / 1000)));
      return undefined;
    }

    const tick = () => {
      const now = getServerTime();
      const remaining = Math.max(0, Math.ceil((targetTime - now) / 1000));
      setSecondsLeft(remaining);
    };

    tick();
    const id = setInterval(tick, 200);
    return () => clearInterval(id);
  }, [targetTime, getServerTime, paused, remainingMs]);

  if (secondsLeft === null) return null;

  const isUrgent = !paused && secondsLeft <= 3;

  return (
    <div style={overlayStyle}>
      {/* Hand icon + instruction */}
      <div style={instructionStyle}>
        <div style={iconStyle}>
          <span style={{ position: 'relative' }}>
            🙋
            <span style={{ position: 'absolute', left: '-0.3em', top: '-0.85em', fontSize: '0.7em' }}>📱</span>
          </span>
        </div>
        <div style={headingStyle}>{t('holdPhoneUp')}</div>
        <div style={subtitleStyle}>{paused ? t('paused') : t('startingIn')}</div>
      </div>

      {/* Countdown number — dimmed while held (MM #190) */}
      <div
        key={paused ? 'paused' : secondsLeft}
        style={{
          ...numberStyle,
          ...(paused ? { color: 'rgba(255,255,255,0.55)' } : {}),
          textShadow: paused ? 'none' : '0 0 30px rgba(126, 211, 33, 0.4)',
        }}
      >
        {secondsLeft}
      </div>

      {/* Bottom instruction */}
      <div style={bottomStyle}>{t('keepScreenOn')}</div>

      <style>{fadeInKeyframes}</style>
    </div>
  );
}

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(0, 0, 0, 0.95)',
  color: '#fff',
  zIndex: 9998,
  fontFamily: 'Work Sans, sans-serif',
};

const instructionStyle = {
  textAlign: 'center',
  marginBottom: '2rem',
  animation: 'fadeIn 0.5s ease',
  fontFamily: 'Work Sans, sans-serif',
};

const iconStyle = {
  fontSize: '3rem',
  marginBottom: '1rem',
  lineHeight: 1.2,
};

const headingStyle = {
  fontSize: '1.25rem',
  fontWeight: 400,
  marginBottom: '0.5rem',
  fontFamily: 'Work Sans, sans-serif',
};

const subtitleStyle = {
  fontSize: '1rem',
  opacity: 0.7,
  fontFamily: 'Work Sans, sans-serif',
};

const numberStyle = {
  fontSize: 'min(30vw, 140px)',
  fontWeight: 500,
  lineHeight: 1,
  fontVariantNumeric: 'tabular-nums',
  transition: 'color 0.3s ease',
  fontFamily: 'Work Sans, sans-serif',
  color: '#7ed321',
};

const bottomStyle = {
  position: 'absolute',
  bottom: 32,
  fontSize: '0.875rem',
  opacity: 0.6,
  fontFamily: 'Work Sans, sans-serif',
};

const fadeInKeyframes = `@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`;
